<script lang="ts">
  /**
   * Kalenteri: Monday-first month grid coloured by the API's day scores,
   * month stats, and a peek card for the tapped day.
   */
  import { apiFetch } from '../lib/api';
  import { navigate } from '../lib/router';
  import { errorMessage } from '../lib/errors';
  import { buildCalendarCells, scoreClass } from '../lib/calendar';
  import { addMonths, fmtDate, fmtKg, monthName, monthTitle, todayKey, toMonthKey, weekdayName, FI_WEEKDAYS_SHORT } from '../lib/format';
  import { plans } from '../lib/plans.svelte';
  import type { CalendarDay, CalendarMonth, Day } from '../lib/types';
  import Icon from '../components/Icon.svelte';
  import LoadState from '../components/LoadState.svelte';
  import Pill from '../components/Pill.svelte';
  import StatTile from '../components/StatTile.svelte';

  // Route params are accepted for the router's component type; this view has none.
  let _props: { params?: Record<string, string> } = $props();

  let today = $derived(todayKey());
  let month = $state(addMonths(new Date(), 0));
  let monthKey = $derived(toMonthKey(month));

  let data = $state<CalendarMonth | null>(null);
  let loading = $state(false);
  let error = $state<string | null>(null);
  let loadedFor = '';
  let selected = $state<string | null>(null);

  async function load(): Promise<void> {
    const target = monthKey;
    loading = true;
    error = null;
    try {
      const d = await apiFetch<CalendarMonth>(`/api/calendar?month=${target}`);
      if (target === monthKey) { data = d; loadedFor = target; }
    } catch (err) {
      if (target === monthKey) error = errorMessage(err);
    } finally {
      if (target === monthKey) loading = false;
    }
  }

  $effect(() => {
    if (loadedFor !== monthKey) {
      data = null;
      selected = null;
      void load();
    }
  });

  let grid = $derived(buildCalendarCells(month.getFullYear(), month.getMonth()));
  let byDate = $derived.by(() => {
    const m = new Map<string, CalendarDay>();
    for (const d of data?.days ?? []) m.set(d.date, d);
    return m;
  });

  /* ---- peek ---- */
  let peek = $state<Day | null>(null);
  let peekLoading = $state(false);
  let peekError = $state<string | null>(null);

  async function openPeek(date: string): Promise<void> {
    selected = date;
    peek = null;
    peekError = null;
    peekLoading = true;
    try {
      const d = await apiFetch<Day>(`/api/days/${date}`);
      if (selected === date) peek = d;
    } catch (err) {
      if (selected === date) peekError = errorMessage(err);
    } finally {
      if (selected === date) peekLoading = false;
    }
  }

  function peekTone(score: number): 'good' | 'warn' | 'bad' | 'plain' {
    return score === 5 ? 'good' : score >= 3 ? 'warn' : score >= 1 ? 'bad' : 'plain';
  }

  function prev(): void { month = addMonths(month, -1); }
  function next(): void { month = addMonths(month, 1); }
</script>

<div class="topbar"><div class="grow"><div class="sub">Historia</div><h1>Kalenteri</h1></div></div>

<div class="card">
  <div class="cal-head">
    <button type="button" aria-label="Edellinen kuukausi" onclick={prev}><Icon name="chevron-left" /></button>
    <h2>{monthTitle(month)}</h2>
    <button type="button" aria-label="Seuraava kuukausi" onclick={next}><Icon name="chevron-right" /></button>
  </div>
  <div class="wk" aria-hidden="true">
    {#each FI_WEEKDAYS_SHORT as w (w)}<span>{w}</span>{/each}
  </div>
  <div class="grid" aria-busy={loading}>
    {#each grid.cells as cell, i (cell.date ?? `blank-${i}`)}
      {#if cell.date === null}
        <div class="day empty"></div>
      {:else if cell.date > today}
        <div class="day future num">{cell.day}</div>
      {:else}
        {@const d = byDate.get(cell.date)}
        {@const cls = d ? scoreClass(d.score, d.touched) : 'g0'}
        <button
          type="button"
          class="day num {cls}"
          class:today={cell.date === today}
          class:sel={selected === cell.date && cell.date !== today}
          aria-label={`${fmtDate(cell.date)}${d ? `, ${d.score}/5 ateriaa` : ''}`}
          onclick={() => openPeek(cell.date ?? '')}
        >
          <span>{cell.day}</span>
          {#if d?.exercise}
            <span class="mv"><Icon name="move" /></span>
          {:else if d && d.score > 0}
            <span class="sc">{d.score}/5</span>
          {/if}
        </button>
      {/if}
    {/each}
  </div>
  {#if !data}
    <LoadState {loading} {error} onretry={load} compact />
  {/if}
  <div class="cal-legend">
    <span><i class="l-good"></i>5/5</span>
    <span><i class="l-warn"></i>3-4/5</span>
    <span><i class="l-bad"></i>1-2/5</span>
    <span><i class="l-none"></i>ei merkintöjä</span>
    <span><i class="l-move"></i>liikunta</span>
  </div>
</div>

<h2 class="section-title">{monthName(month, true)}</h2>
<div class="stat-row">
  <StatTile tone="good" value={String(data?.summary.fullDays ?? '-')} label="päivää 5/5" />
  <StatTile tone="move" value={String(data?.summary.exerciseDays ?? '-')} label="liikuntapäivää" />
  <StatTile value={String(data?.summary.loggedDays ?? '-')} label="päivää merkattu" />
</div>

{#if selected}
  <div class="card daypeek">
    {#if !peek}
      <LoadState loading={peekLoading} error={peekError} onretry={() => openPeek(selected ?? '')} compact />
    {:else}
      <div class="row peek-head">
        <div class="grow">
          <div class="eyebrow">{weekdayName(selected)}</div>
          <h3>{fmtDate(selected)}</h3>
        </div>
        <Pill tone={peekTone(peek.score)}>{peek.score}/5 ateriaa</Pill>
      </div>
      <div class="pills">
        <Pill tone="accent">{plans.titleOf(peek.planId)}</Pill>
        {#if peek.exercise}
          <Pill tone="move"><Icon name="move" /> {peek.exercise.type}{peek.exercise.minutes ? ` ${peek.exercise.minutes} min` : ''}</Pill>
        {:else}
          <Pill>Ei liikuntaa</Pill>
        {/if}
        {#if peek.weightKg !== null}
          <Pill num>{fmtKg(peek.weightKg)} kg</Pill>
        {/if}
      </div>
      <button type="button" class="btn ghost" onclick={() => navigate(selected === today ? '/' : `/paiva/${selected}`)}>
        Avaa ja muokkaa päivää
      </button>
    {/if}
  </div>
{/if}

<style>
  .topbar {
    position: sticky;
    top: 0;
    z-index: 5;
    background: linear-gradient(var(--viisi-ground) 80%, transparent);
    padding: 18px 0 12px;
    display: flex;
    align-items: center;
    gap: 10px;
  }
  .topbar h1 { font-size: 26px; font-weight: 800; letter-spacing: -0.01em; flex: 1; }
  .topbar .sub { color: var(--viisi-ink-2); font-size: 13px; }
  .grow { flex: 1; min-width: 0; }
  .row { display: flex; align-items: center; gap: 10px; }
  .card {
    background: var(--viisi-surface);
    border-radius: var(--viisi-radius);
    padding: 14px;
    box-shadow: var(--viisi-shadow);
  }
  .section-title { margin: 22px 0 10px; font-size: 17px; font-weight: 700; }
  .eyebrow {
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: var(--viisi-ink-3);
  }

  .cal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 10px; }
  .cal-head h2 { font-size: 20px; font-weight: 800; }
  .cal-head button {
    width: 34px;
    height: 34px;
    border-radius: 50%;
    background: var(--viisi-surface-2);
    display: grid;
    place-items: center;
  }
  .cal-head button :global(svg) { width: 16px; height: 16px; }
  .wk {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    gap: 4px;
    font-size: 11px;
    color: var(--viisi-ink-3);
    text-align: center;
    font-weight: 600;
    margin-bottom: 6px;
  }
  .grid { display: grid; grid-template-columns: repeat(7, 1fr); gap: 4px; }
  .day {
    aspect-ratio: 1;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 2px;
    font-size: 13px;
    font-weight: 700;
    background: var(--viisi-surface-2);
    color: var(--viisi-ink);
    position: relative;
    width: 100%;
  }
  .day.empty { background: transparent; }
  .day.future {
    color: var(--viisi-ink-3);
    background: transparent;
    border: 1px dashed var(--viisi-line);
    font-weight: 500;
  }
  .day.g5 { background: var(--viisi-good); color: var(--viisi-good-ink); }
  .day.g3 { background: var(--viisi-warn); color: var(--viisi-warn-ink); }
  .day.g1 { background: var(--viisi-bad); color: var(--viisi-bad-ink); }
  .day.g0 { background: var(--viisi-surface-2); color: var(--viisi-ink-3); }
  .day.today { outline: 2px solid var(--viisi-ink); outline-offset: 1px; }
  .day.sel { box-shadow: 0 0 0 3px var(--viisi-accent); }
  .day .mv {
    width: 14px;
    height: 14px;
    border-radius: 50%;
    background: var(--viisi-move);
    color: var(--viisi-move-ink);
    display: grid;
    place-items: center;
  }
  .day .mv :global(svg) { width: 9px; height: 9px; }
  .day .sc { font-size: 9px; font-weight: 700; opacity: 0.85; line-height: 1; }

  .cal-legend {
    display: flex;
    flex-wrap: wrap;
    gap: 10px 14px;
    margin: 12px 2px 0;
    font-size: 12px;
    color: var(--viisi-ink-2);
  }
  .cal-legend i {
    display: inline-block;
    width: 12px;
    height: 12px;
    border-radius: 4px;
    vertical-align: -2px;
    margin-right: 5px;
  }
  .l-good { background: var(--viisi-good); }
  .l-warn { background: var(--viisi-warn); }
  .l-bad { background: var(--viisi-bad); }
  .l-none { background: var(--viisi-surface-3); }
  .l-move { background: var(--viisi-move); border-radius: 50% !important; }

  .stat-row { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }

  .daypeek { margin-top: 12px; }
  .peek-head { margin-bottom: 8px; }
  .daypeek h3 { font-size: 18px; }
  .pills { display: flex; flex-wrap: wrap; gap: 6px; margin: 0 0 10px; }
  .btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 11px 16px;
    border-radius: 12px;
    font-weight: 700;
    font-size: 14px;
    width: 100%;
  }
  .btn.ghost { background: var(--viisi-accent-soft); color: var(--viisi-accent); }
</style>
